import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Shield, Gift, Users, Smartphone, Copy, Check, Sparkles, QrCode } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import Card from '../components/ui/Card';

const Donate = () => {
  const toast = useToast();
  const [copiedUPI, setCopiedUPI] = React.useState(false);

  useEffect(() => {
    document.title = 'Support Our Mission - Pragyavan Wellness Centre';
  }, []);

  const copyUPIId = () => {
    navigator.clipboard.writeText('8904568399@hdfc');
    setCopiedUPI(true);
    toast.showSuccess('UPI ID Copied!', 'You can now paste it in your payment app.');
    setTimeout(() => setCopiedUPI(false), 2000);
  };

  const paymentSteps = [
    { step: 1, text: 'Open any UPI app (GPay, PhonePe, Paytm, etc.)' },
    { step: 2, text: 'Scan the QR code or use the UPI ID' },
    { step: 3, text: 'Enter your donation amount' },
    { step: 4, text: 'Complete the payment' }
  ];

  const trustBadges = [
    {
      icon: Shield,
      title: 'Secure Payment',
      description: 'Safe and secure UPI transactions',
      color: 'sage-green',
      gradient: 'from-sage-green/20 to-sage-green/5'
    },
    {
      icon: Gift,
      title: 'Tax Benefits',
      description: 'Donations eligible for tax deduction under 80G',
      color: 'pragyavan-gold',
      gradient: 'from-pragyavan-gold/20 to-pragyavan-gold/5'
    },
    {
      icon: Users,
      title: 'Community Impact',
      description: 'Empowering community wellness initiatives',
      color: 'pragyavan-brown',
      gradient: 'from-pragyavan-brown/15 to-pragyavan-brown/5'
    }
  ];

  const renderDonationPage = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="space-y-12"
    >
      {/* Header Section */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-50 to-pink-50 border border-red-100 text-pragyavan-brown text-xs font-semibold mb-6 shadow-sm"
        >
          <Heart className="w-4 h-4 text-red-500 fill-red-500 animate-pulse" />
          <span>Make a Difference</span>
        </motion.div>
        <h1 className="text-4xl md:text-5xl font-display font-bold text-pragyavan-brown mb-5">
          Support Our <span className="text-gradient-gold">Mission</span>
        </h1>
        <p className="text-warm-gray text-lg max-w-2xl mx-auto leading-relaxed">
          Your contribution helps us expand our mission of bringing holistic wellness, mindfulness, and inner peace to our community.
        </p>
      </div>

      {/* UPI Payment Section */}
      <Card className="shadow-xl shadow-pragyavan-brown/5 border border-white/60 bg-white/80 backdrop-blur-sm overflow-hidden relative">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-pragyavan-gold/10 via-pragyavan-gold/5 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-sage-green/10 to-transparent rounded-full blur-3xl pointer-events-none" />

        <Card.Body className="relative z-10">
          {/* Section Header */}
          <div className="text-center mb-10">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-pragyavan-gold/20 to-accent-gold/20 text-pragyavan-brown text-sm font-semibold mb-4 shadow-sm border border-pragyavan-gold/20"
            >
              <Smartphone className="w-4 h-4" />
              <span>Quick UPI Payment</span>
            </motion.div>
            <h3 className="text-2xl md:text-3xl font-display font-bold text-pragyavan-brown mb-2">Scan & Pay</h3>
            <p className="text-warm-gray">Use any UPI app to donate instantly</p>
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-16">
            {/* QR Code */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="flex-shrink-0"
            >
              <div className="relative group">
                {/* Glow effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-pragyavan-gold/20 via-accent-gold/20 to-pragyavan-gold/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative bg-white p-5 rounded-3xl shadow-xl border-2 border-pragyavan-gold/30 group-hover:border-pragyavan-gold/50 transition-all duration-300">
                  {/* QR Badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-pragyavan-brown text-pragyavan-gold px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg">
                    <QrCode className="w-3 h-3" />
                    <span>Scan Me</span>
                  </div>

                  <img
                    src="/images/payments/upi-qr-code.png"
                    alt="UPI QR Code for Donation"
                    className="w-56 h-56 md:w-64 md:h-64 object-contain"
                  />

                  {/* UPI ID below QR */}
                  <div className="mt-4 pt-4 border-t border-warm-gray/10 text-center">
                    <p className="text-xs text-warm-gray mb-1">UPI ID</p>
                    <code className="text-sm font-mono font-bold text-pragyavan-brown">8904568399@hdfc</code>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* UPI Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex-1 max-w-md w-full"
            >
              <div className="space-y-5">
                {/* Copy UPI ID Card */}
                <div className="bg-gradient-to-br from-white to-pragyavan-cream/30 p-5 rounded-2xl border border-pragyavan-gold/20 shadow-lg shadow-pragyavan-gold/5 hover:shadow-xl hover:shadow-pragyavan-gold/10 transition-all duration-300">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-warm-gray mb-1 font-medium uppercase tracking-wider">UPI ID</p>
                      <code className="text-xl font-mono font-bold text-pragyavan-brown">
                        8904568399@hdfc
                      </code>
                    </div>
                    <motion.button
                      onClick={copyUPIId}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`p-3 rounded-xl transition-all duration-300 ${
                        copiedUPI
                          ? 'bg-green-100 text-green-600'
                          : 'bg-pragyavan-gold/10 hover:bg-pragyavan-gold/20 text-pragyavan-brown'
                      }`}
                      title="Copy UPI ID"
                    >
                      {copiedUPI ? (
                        <Check className="w-6 h-6" />
                      ) : (
                        <Copy className="w-6 h-6" />
                      )}
                    </motion.button>
                  </div>
                  {copiedUPI && (
                    <motion.p
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-green-600 mt-2 font-medium"
                    >
                      Copied to clipboard!
                    </motion.p>
                  )}
                </div>

                {/* How to Pay Steps */}
                <div className="bg-gradient-to-br from-pragyavan-gold/10 via-pragyavan-gold/5 to-transparent p-5 rounded-2xl border border-pragyavan-gold/10">
                  <h4 className="text-sm font-bold text-pragyavan-brown mb-4 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pragyavan-gold" />
                    How to pay:
                  </h4>
                  <div className="space-y-3">
                    {paymentSteps.map((item, index) => (
                      <motion.div
                        key={item.step}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="flex items-start gap-3 group"
                      >
                        <div className="w-6 h-6 rounded-full bg-pragyavan-brown text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-pragyavan-gold group-hover:text-pragyavan-brown transition-colors duration-300">
                          {item.step}
                        </div>
                        <p className="text-sm text-pragyavan-brown/80 leading-relaxed group-hover:text-pragyavan-brown transition-colors duration-300">
                          {item.text}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Card.Body>
      </Card>

      {/* Trust Badges */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid md:grid-cols-3 gap-6"
      >
        {trustBadges.map((badge, index) => {
          const IconComponent = badge.icon;
          return (
            <motion.div
              key={badge.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group relative"
            >
              {/* Hover glow */}
              <div className={`absolute inset-0 bg-gradient-to-br ${badge.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10`} />

              <div className="text-center p-8 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/60 shadow-lg shadow-pragyavan-brown/3 group-hover:shadow-xl group-hover:bg-white/90 transition-all duration-300 h-full">
                <div className={`w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br ${badge.gradient} flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300`}>
                  <IconComponent className={`w-8 h-8 text-${badge.color}`} />
                </div>
                <h4 className="font-display font-bold text-lg text-pragyavan-brown mb-2 group-hover:text-pragyavan-gold transition-colors duration-300">
                  {badge.title}
                </h4>
                <p className="text-sm text-warm-gray leading-relaxed">{badge.description}</p>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Bottom CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="text-center pt-8"
      >
        <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pragyavan-gold/10 via-pragyavan-cream to-sage-green/10 border border-pragyavan-gold/20">
          <Heart className="w-4 h-4 text-red-500" />
          <span className="text-sm text-pragyavan-brown font-medium">Every contribution makes a difference</span>
          <Heart className="w-4 h-4 text-red-500" />
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative min-h-screen"
    >
      {/* Page background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-pragyavan-gold/5 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-0 w-80 h-80 bg-sage-green/5 rounded-full blur-3xl" />
        <div className="absolute bottom-40 right-1/4 w-64 h-64 bg-red-500/3 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 pt-24 pb-12 relative z-10">
        <div className="max-w-5xl mx-auto">
          {renderDonationPage()}
        </div>
      </div>
    </motion.div>
  );
};

export default Donate;